import { useObjectives } from "@/hooks/useObjectives";
import { useKeyResults } from "@/hooks/useKeyResults";
import { usePpps, RANKED_PPP_PARAMS } from "@/hooks/usePpps";

const OVERVIEW_LIMIT = 500;

interface OkrOverviewOptions {
  /** False until the cycle list has loaded, so the objectives page is fetched once, already scoped. */
  cyclesReady: boolean;
  /** KRs and PPPs are only needed by the Coverage view. */
  coverage: boolean;
}

/** Company-wide dataset for the /okrs dashboard (pacing, category cards, coverage) — one page of
 * up to 500 rows each, independent of the paginated objectives list used by ObjectiveList. */
export function useOkrOverview(cyclePid: string | undefined, { cyclesReady, coverage }: OkrOverviewOptions) {
  const objectivesQuery = useObjectives(
    { cyclePid: cyclePid || undefined, page: 1, limit: OVERVIEW_LIMIT, sortBy: "orderIndex", sortOrder: "ASC" },
    cyclesReady,
  );
  const keyResultsQuery = useKeyResults(
    { page: 1, limit: OVERVIEW_LIMIT, sortBy: "orderIndex", sortOrder: "ASC" },
    coverage,
  );
  const pppsQuery = usePpps(RANKED_PPP_PARAMS, coverage);

  return {
    objectives: objectivesQuery.data?.data ?? [],
    keyResults: keyResultsQuery.data?.data ?? [],
    ppps: pppsQuery.data?.data ?? [],
    isLoading: objectivesQuery.isLoading || keyResultsQuery.isLoading || pppsQuery.isLoading,
    isError: objectivesQuery.isError || keyResultsQuery.isError || pppsQuery.isError,
    /** Any list has more rows than one page returned — coverage can no longer claim completeness. */
    truncated: [objectivesQuery, keyResultsQuery, pppsQuery].some(
      (q) => (q.data?.total ?? 0) > (q.data?.data.length ?? 0),
    ),
  };
}
